import { useState } from 'react'
import { api } from '../lib/api'
import type { Approval } from '../lib/api'
import { Button } from './ui/button'
import { Card } from './ui/card'

/**
 * ApprovalCard is the inline action card for a tool the agent may not run
 * without permission.
 *
 * It is deliberately a card in the transcript rather than a modal: the run is
 * parked server-side and will wait, so interrupting the user is unnecessary, and
 * the decision belongs in the conversation where it will still make sense when
 * read back tomorrow.
 *
 * Both buttons are equally prominent. Making "Approve" the obvious one would
 * turn a security gate into a formality — the whole point is that the user
 * actually reads what is about to run.
 */
export function ApprovalCard({
  approval,
  onDecided,
}: {
  approval: Approval
  onDecided: (id: string) => void
}) {
  const [pending, setPending] = useState(false)
  const [error, setError] = useState('')

  async function decide(approved: boolean) {
    setPending(true)
    setError('')
    try {
      await api.decideApproval(approval.id, approved)
      onDecided(approval.id)
    } catch (err) {
      // The card stays put on failure. Removing it would leave the run parked
      // with nothing on screen able to release it.
      setError(err instanceof Error ? err.message : 'Could not record that decision.')
      setPending(false)
    }
  }

  const args = approval.args ? JSON.stringify(approval.args, null, 2) : ''

  return (
    <Card
      className="mx-4 my-2 gap-0 border-amber-500/40 bg-amber-500/10 p-4"
      data-testid="approval-card"
    >
      <p className="text-sm font-semibold text-amber-700 dark:text-amber-400">
        Agent wants to run <code>{approval.tool_name || 'a tool'}</code>
      </p>

      {args && (
        <pre className="mt-2 overflow-x-auto whitespace-pre-wrap rounded-sm bg-background p-2 font-mono text-xs">
          {args}
        </pre>
      )}

      {error && <p className="mt-2 text-xs text-destructive">{error}</p>}

      <div className="mt-3 flex gap-2">
        <Button
          size="sm"
          className="bg-green-600 text-white hover:bg-green-700"
          disabled={pending}
          onClick={() => decide(true)}
          data-testid="approve"
        >
          {pending ? 'Approving…' : 'Approve'}
        </Button>
        <Button
          size="sm"
          variant="outline"
          className="border-destructive text-destructive hover:bg-destructive/10"
          disabled={pending}
          onClick={() => decide(false)}
          data-testid="deny"
        >
          {pending ? 'Denying…' : 'Deny'}
        </Button>
      </div>
    </Card>
  )
}
